import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";

/**
 * The Validator interface represents the validity states that an element can be in, with respect to constraint validation.
 */
function Validator(_ref) {
  let {
    children
  } = _ref;
  // An object of all the form controls' validity keyed by form control name
  const [validityStates, setValidityStates] = useState({});

  // Use a Proxy object so consumers can still reference properties that may
  // not yet be in the validityStates object.
  const validity = useMemo(() => new Proxy({}, {
    get: (target, name) => {
      const {
        [name]: validityState
      } = validityStates;
      return validityState ? validityState : {};
    }
  }), [validityStates]);
  function updateValidityStates(event) {
    const {
      target: {
        name,
        validity: validityState,
        validationMessage
      }
    } = event;
    if (validityState) {
      validityState.validationMessage = validationMessage;
    }
    if (name) {
      setValidityStates(prev => ({
        ...prev,
        [name]: validityState
      }));
    }
  }
  function checkValidity(event) {
    const target = event.target;
    const isValid = target.checkValidity();
    if (isValid) {
      updateValidityStates(event);
    }
    return isValid;
  }
  function resetValidity() {
    let keys = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
    setValidityStates(prev => ({
      // If keys are passed, retain the other validity states, otherwise reset all
      ...(keys.length !== 0 ? prev : {}),
      // Only override any given keys
      ...keys.reduce((acc, v) => ({
        ...acc,
        [v]: {}
      }), {})
    }));
  }
  return /*#__PURE__*/React.createElement("div", {
    onInvalid: updateValidityStates
  }, children({
    checkValidity,
    resetValidity,
    validity
  }));
}
Validator.propTypes = {
  /** Function called with properties `checkValidity` (returns `true` or `false` depending if target is valid),
   * `resetValidity` (accepts an array of keys to reset validation, defaults to all keys, resets the forms validation),
   * and `validity` (key to match named inputs with value set to [ValidityState](https://developer.mozilla.org/en-US/docs/Web/API/ValidityState) also includes validationMessage) */
  children: PropTypes.func.isRequired
};
export default Validator;